import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import {
  Sparkles, Compass, Brain, Stethoscope, Wine, Dices, Flame, HeartHandshake, Leaf,
  Home, Shield, Heart, RefreshCw, Building2, HandHeart, LifeBuoy, Phone, MessageSquare,
  BookOpen, Download, Moon, Drama, Sprout, ChevronDown, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import AskAurea from "@/components/resources/AskAurea";
import SupportSection from "@/components/resources/SupportSection";
import RecoverySection from "@/components/resources/RecoverySection";
import FacilitiesSection from "@/components/resources/FacilitiesSection";
import MentorshipSection from "@/components/resources/MentorshipSection";

const CAT_META = {
  "Mental Health": { icon: Brain, order: 1 },
  "Disorders": { icon: Stethoscope, order: 2 },
  "Substance Use": { icon: Wine, order: 3 },
  "Behavioral Addictions": { icon: Dices, order: 4 },
  "Emotion Regulation": { icon: Flame, order: 5 },
  "Family & Relationships": { icon: HeartHandshake, order: 6 },
  "Life Events": { icon: Leaf, order: 7 },
};

const GLOSSARY = [
  { icon: Moon, term: "The Shadow",
    short: "The parts of yourself you've hidden away or disowned — often the traits you judge most harshly. Meeting them with honesty is where real wholeness begins.",
    long: "Jung believed nothing disappears when we repress it — it simply moves into the unconscious \"shadow.\" The impulses, fears and even gifts we won't claim keep shaping us from the dark. Turning toward the shadow with curiosity rather than shame lets that trapped energy return to you as strength, creativity and self-acceptance." },
  { icon: Drama, term: "The Persona",
    short: "The mask you wear for the world — the polished self you show at work, online, or in public. Useful, but painful when you forget it isn't the whole you.",
    long: "The persona is the social face we craft to belong and function. It's healthy in measure, but when we over-identify with it we lose contact with who we actually are, and life starts to feel hollow or performative. Growth means wearing the mask by choice — and knowing how to set it down when you're safe." },
  { icon: Sprout, term: "Individuation",
    short: "The lifelong journey of becoming your true, whole self — integrating your light and shadow into one authentic person. It's the quiet aim beneath all of Aurea.",
    long: "Individuation is Jung's word for the process of becoming undivided: gathering the scattered, denied and unconscious parts of yourself into a coherent whole. It isn't about perfection — it's about wholeness, living from your own center rather than others' expectations. Every reflection, mood check and shadow practice here is a small step along that path." },
];

const HAVEN = [
  { label: "988 Suicide & Crisis Lifeline", detail: "Call or text, 24/7 — free, confidential", action: "tel:988", cta: "Call 988", second: { action: "sms:988", cta: "Text 988" } },
  { label: "Crisis Text Line", detail: "Text HOME to 741741 for a trained counselor", action: "sms:741741?&body=HOME", cta: "Text HOME" },
  { label: "SAMHSA National Helpline", detail: "Treatment & referrals, 24/7 — 1-800-662-4357", action: "tel:18006624357", cta: "Call SAMHSA" },
];

const READING = [
  { title: "Man's Search for Meaning", author: "Viktor Frankl", why: "Finding purpose even inside suffering." },
  { title: "The Body Keeps the Score", author: "Bessel van der Kolk", why: "How trauma lives in the body — and heals." },
  { title: "Memories, Dreams, Reflections", author: "Carl Jung", why: "Jung's own journey toward wholeness." },
  { title: "The Gifts of Imperfection", author: "Brené Brown", why: "Softening shame and self-judgment." },
];

const PRINT_JOURNAL = `AUREA — DAILY REFLECTION JOURNAL\n(print or keep on your device)\n\nDate: __________________\n\n1. How is my heart feeling right now, in one word?\n   ______________________________________________\n\n2. What is one thing weighing on me today?\n   ______________________________________________\n   ______________________________________________\n\n3. What did I need today that I didn't ask for?\n   ______________________________________________\n\n4. One shadow I noticed (a reaction that surprised me):\n   ______________________________________________\n\n5. One small kindness I can offer myself tomorrow:\n   ______________________________________________\n\n"Wholeness, not perfection." — C.G. Jung\n`;

const PRINT_GROUNDING = `AUREA — GROUNDING & GRATITUDE SHEET\n\nWhen the mind races, come back to your senses:\n\n5 things I can SEE:  ____________________________\n4 things I can TOUCH: ___________________________\n3 things I can HEAR:  ___________________________\n2 things I can SMELL: ___________________________\n1 thing I can TASTE:  ___________________________\n\nThree small things I'm grateful for today:\n  •  _____________________________________________\n  •  _____________________________________________\n  •  _____________________________________________\n\nBreathe in for 4 · hold for 4 · out for 6. Repeat 5 times.\n`;

const download = (name, content) => {
  const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
};

const DisorderCategory = ({ list }) => (
  <Accordion type="single" collapsible className="space-y-2">
    {list.map((it) => (
      <AccordionItem key={it.id} value={it.id} className="bg-white rounded-2xl border border-linen-3 px-4" data-testid={`topic-${it.id}`}>
        <AccordionTrigger className="hover:no-underline py-4 font-serif text-xl text-ink" data-testid={`topic-trigger-${it.id}`}>{it.name}</AccordionTrigger>
        <AccordionContent className="pb-5 space-y-3">
          <p className="text-ink-2 leading-relaxed">{it.overview}</p>
          <div>
            <p className="text-sm font-medium text-ink mb-1">Signs you might notice</p>
            <ul className="text-sm text-ink-2 list-disc list-inside space-y-1">{it.signs.map((s, i) => <li key={i}>{s}</li>)}</ul>
          </div>
          <div>
            <p className="text-sm font-medium text-ink mb-1">Gentle tools</p>
            <div className="flex flex-wrap gap-1.5">{it.tools.map((t, i) => <span key={i} className="text-xs bg-calm text-calm-text px-2.5 py-1 rounded-full">{t}</span>)}</div>
          </div>
          <AskAurea topic={it.name} placeholder={`What's happening with ${it.name.toLowerCase()} for you?`} />
        </AccordionContent>
      </AccordionItem>
    ))}
  </Accordion>
);

const Resources = () => {
  const [items, setItems] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [openTerm, setOpenTerm] = useState(null);
  const [archOpen, setArchOpen] = useState(false);
  const [archInput, setArchInput] = useState("");
  const [archResult, setArchResult] = useState(null);
  const [archLoading, setArchLoading] = useState(false);

  useEffect(() => { api.get("/resources/disorders").then(({ data }) => setItems(data)).catch(() => {}); }, []);

  const discover = async () => {
    if (!archInput.trim()) { toast.info("Tell Aurea a little about yourself first."); return; }
    setArchLoading(true);
    try {
      const { data } = await api.post("/archetype/discover", { description: archInput });
      setArchResult(data);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setArchLoading(false); }
  };

  const categories = [...new Set(items.map((i) => i.category))]
    .sort((a, b) => (CAT_META[a]?.order || 99) - (CAT_META[b]?.order || 99));

  const SelfDiscovery = (
    <div className="relative overflow-hidden rounded-3xl bg-brand text-linen p-8" data-testid="archetype-banner">
      <span className="w-12 h-12 rounded-2xl bg-linen/15 flex items-center justify-center mb-4"><Compass className="w-6 h-6 text-linen" /></span>
      <h2 className="font-serif text-3xl sm:text-4xl">Discover your archetype</h2>
      <p className="text-linen/85 mt-2 leading-relaxed max-w-xl">Share a little about yourself and Aurea will reflect the Jungian archetype most alive in you.</p>
      <Button onClick={() => { setArchOpen(true); setArchResult(null); setArchInput(""); }} className="rounded-full bg-linen text-brand hover:bg-linen-2 mt-5" data-testid="discover-archetype-button"><Sparkles className="w-4 h-4 mr-2" /> Begin discovery</Button>
    </div>
  );

  const sections = [
    { v: "self", label: "Self-Discovery", icon: Compass, body: SelfDiscovery },
    ...categories.map((cat) => ({
      v: cat.toLowerCase().replace(/[^a-z]+/g, "-"),
      label: cat, icon: CAT_META[cat]?.icon || Brain,
      body: cat === "Mental Health" ? (
        <div className="space-y-6">
          <SupportSection category="mental_health" />
          <div>
            <h3 className="font-serif text-2xl text-brand mb-2">Explore by topic</h3>
            <DisorderCategory list={items.filter((i) => i.category === cat)} />
          </div>
        </div>
      ) : (
        <DisorderCategory list={items.filter((i) => i.category === cat)} />
      ),
    })),
    { v: "housing", label: "Housing & Basic Needs", icon: Home, body: <SupportSection category="housing" /> },
    { v: "veterans", label: "Veterans & PTSD", icon: Shield, body: <SupportSection category="veterans" /> },
    { v: "lgbtq", label: "LGBTQ+ Support", icon: Heart, body: <SupportSection category="lgbtq" /> },
    { v: "recovery", label: "Recovery & 12-Step", icon: RefreshCw, body: <RecoverySection /> },
    { v: "facilities", label: "Treatment Facilities", icon: Building2, body: <FacilitiesSection /> },
    { v: "mentorship", label: "Mentorship", icon: HandHeart, body: <MentorshipSection /> },
  ];

  return (
    <div className="space-y-6" data-testid="resources-page">
      <div>
        <h1 className="font-serif text-4xl sm:text-5xl text-ink">Resources & Support</h1>
        <p className="text-ink-2 mt-2">Three calm doorways — understand yourself, reach safety fast, and keep growing.</p>
      </div>

      {/* CARD 1 — The Jungian Glossary */}
      <div className="rounded-3xl border border-linen-3 p-7 sm:p-8 backdrop-blur-md"
        style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.85), rgba(240,233,224,0.6))" }}
        data-testid="resource-card-glossary">
        <div className="flex items-center gap-3 mb-1">
          <span className="w-11 h-11 rounded-2xl bg-brand/10 flex items-center justify-center"><BookOpen className="w-5 h-5 text-brand" /></span>
          <h2 className="font-serif text-2xl sm:text-3xl text-ink">The Jungian Glossary</h2>
        </div>
        <p className="text-sm text-ink-2 mb-5">Plain-language words for the inner world — so the ideas behind Aurea feel familiar, not clinical.</p>
        <div className="space-y-3">
          {GLOSSARY.map((g) => {
            const isOpen = openTerm === g.term;
            return (
              <div key={g.term} className="rounded-2xl bg-white/70 border border-linen-3 p-4" data-testid={`glossary-${g.term.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="flex items-start gap-3">
                  <span className="w-9 h-9 rounded-xl bg-calm flex items-center justify-center shrink-0"><g.icon className="w-4 h-4 text-brand" /></span>
                  <div className="min-w-0 flex-1">
                    <p className="font-serif text-xl text-ink">{g.term}</p>
                    <p className="text-sm text-ink-2 leading-relaxed mt-1">{g.short}</p>
                    {isOpen && <p className="text-sm text-ink-2 leading-relaxed mt-3 pt-3 border-t border-linen-3">{g.long}</p>}
                    <button onClick={() => setOpenTerm(isOpen ? null : g.term)} data-testid={`glossary-expand-${g.term.replace(/\s+/g, "-").toLowerCase()}`}
                      className="flex items-center gap-1 text-sm text-accent font-medium mt-2 hover:underline">
                      {isOpen ? "Show less" : "Expand to read"} <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* CARD 2 — The Safe Haven */}
      <div className="rounded-3xl border border-accent/25 p-7 sm:p-8 backdrop-blur-md"
        style={{ background: "linear-gradient(135deg, rgba(255,247,244,0.9), rgba(250,232,226,0.7))" }}
        data-testid="resource-card-haven">
        <div className="flex items-center gap-3 mb-1">
          <span className="w-11 h-11 rounded-2xl bg-accent/15 flex items-center justify-center"><LifeBuoy className="w-5 h-5 text-accent" /></span>
          <h2 className="font-serif text-2xl sm:text-3xl text-ink">The Safe Haven</h2>
        </div>
        <p className="text-sm text-ink-2 mb-5">Immediate, always-on lifelines — one tap connects you to a real person, any hour, at no cost.</p>
        <div className="space-y-3">
          {HAVEN.map((h) => (
            <div key={h.label} className="rounded-2xl bg-white/80 border border-linen-3 p-4 flex items-center gap-3 flex-wrap" data-testid={`haven-${h.label.split(" ")[0].toLowerCase()}`}>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-ink">{h.label}</p>
                <p className="text-sm text-ink-2">{h.detail}</p>
              </div>
              <a href={h.action} className="rounded-full bg-accent hover:bg-accent-hover text-linen px-5 py-2.5 text-sm font-medium flex items-center gap-1.5 shrink-0" data-testid={`haven-call-${h.label.split(" ")[0].toLowerCase()}`}>
                <Phone className="w-4 h-4" /> {h.cta}
              </a>
              {h.second && (
                <a href={h.second.action} className="rounded-full border border-accent text-accent hover:bg-accent hover:text-linen px-5 py-2.5 text-sm font-medium flex items-center gap-1.5 shrink-0">
                  <MessageSquare className="w-4 h-4" /> {h.second.cta}
                </a>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* CARD 3 — External Growth Media */}
      <div className="rounded-3xl border border-linen-3 p-7 sm:p-8 backdrop-blur-md"
        style={{ background: "linear-gradient(135deg, rgba(238,244,241,0.85), rgba(255,255,255,0.6))" }}
        data-testid="resource-card-media">
        <div className="flex items-center gap-3 mb-1">
          <span className="w-11 h-11 rounded-2xl bg-brand/10 flex items-center justify-center"><Sprout className="w-5 h-5 text-brand" /></span>
          <h2 className="font-serif text-2xl sm:text-3xl text-ink">External Growth Media</h2>
        </div>
        <p className="text-sm text-ink-2 mb-5">Gentle reading and printable journal pages — nourishment you can carry beyond the screen.</p>

        <p className="text-xs uppercase tracking-widest text-ink-mute mb-2">Calming reads</p>
        <div className="grid sm:grid-cols-2 gap-3 mb-6">
          {READING.map((r) => (
            <div key={r.title} className="rounded-2xl bg-white/70 border border-linen-3 p-4" data-testid="reading-item">
              <p className="font-medium text-ink text-[15px]">{r.title}</p>
              <p className="text-xs text-ink-mute">{r.author}</p>
              <p className="text-sm text-ink-2 mt-1 leading-relaxed">{r.why}</p>
            </div>
          ))}
        </div>

        <p className="text-xs uppercase tracking-widest text-ink-mute mb-2">Downloadable printables</p>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => download("Aurea-Daily-Reflection-Journal.txt", PRINT_JOURNAL)} data-testid="download-journal"
            className="rounded-full bg-brand hover:bg-brand-hover text-linen px-5 py-2.5 text-sm flex items-center gap-2">
            <Download className="w-4 h-4" /> Daily Reflection Journal
          </button>
          <button onClick={() => download("Aurea-Grounding-Gratitude-Sheet.txt", PRINT_GROUNDING)} data-testid="download-grounding"
            className="rounded-full border border-brand text-brand hover:bg-brand hover:text-linen px-5 py-2.5 text-sm flex items-center gap-2">
            <Download className="w-4 h-4" /> Grounding & Gratitude Sheet
          </button>
        </div>
      </div>

      {/* Full support hub — tucked away to keep the page calm */}
      <button onClick={() => setShowAll((v) => !v)} data-testid="browse-all-toggle"
        className="w-full flex items-center justify-between rounded-3xl bg-linen-2 border border-linen-3 px-6 py-5 hover:border-brand transition-colors">
        <span className="flex items-center gap-3 font-serif text-xl text-ink">
          <span className="w-10 h-10 rounded-2xl bg-brand/10 flex items-center justify-center"><Compass className="w-5 h-5 text-brand" /></span>
          Browse all support topics & directories
        </span>
        <ChevronRight className={`w-5 h-5 text-ink-mute transition-transform ${showAll ? "rotate-90" : ""}`} />
      </button>

      {showAll && (
        <Accordion type="single" collapsible className="space-y-3" data-testid="resources-hub">
          {sections.map((s) => (
            <AccordionItem key={s.v} value={s.v} className="bg-linen-2 rounded-3xl border border-linen-3 px-5 sm:px-6" data-testid={`section-${s.v}`}>
              <AccordionTrigger className="hover:no-underline py-5" data-testid={`section-trigger-${s.v}`}>
                <span className="flex items-center gap-3 font-serif text-2xl text-ink">
                  <span className="w-10 h-10 rounded-2xl bg-brand/10 flex items-center justify-center"><s.icon className="w-5 h-5 text-brand" /></span>
                  {s.label}
                </span>
              </AccordionTrigger>
              <AccordionContent className="pb-6 pt-2">{s.body}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}

      <Dialog open={archOpen} onOpenChange={setArchOpen}>
        <DialogContent className="bg-linen rounded-3xl max-w-lg max-h-[88vh] overflow-y-auto" data-testid="archetype-dialog">
          <DialogHeader>
            <span className="text-xs text-accent uppercase tracking-wide">Jungian archetypes</span>
            <DialogTitle className="font-serif text-3xl text-ink">Discover your archetype</DialogTitle>
          </DialogHeader>
          {!archResult ? (
            <>
              <p className="text-ink-2 text-sm">How do you typically face challenges? What do you long for, and what do you fear? Write freely.</p>
              <Textarea value={archInput} onChange={(e) => setArchInput(e.target.value)} placeholder="When life gets hard, I tend to…  What I most want is…  What I avoid is…" className="rounded-2xl bg-white border-linen-3 min-h-[120px]" data-testid="archetype-input" />
              <Button onClick={discover} disabled={archLoading} className="rounded-full bg-brand hover:bg-brand-hover text-linen w-full" data-testid="archetype-submit">{archLoading ? "Reflecting…" : "Reveal my archetype"}</Button>
            </>
          ) : (
            <div className="space-y-4" data-testid="archetype-result">
              <div className="text-center">
                <p className="font-serif text-4xl text-brand">{archResult.archetype}</p>
                {archResult.tagline && <p className="italic text-ink-2 font-serif text-lg mt-1">{archResult.tagline}</p>}
              </div>
              <p className="text-ink-2 leading-relaxed">{archResult.summary}</p>
              {archResult.strengths?.length > 0 && (
                <div><p className="text-sm font-medium text-ink mb-1">Strengths</p>
                  <div className="flex flex-wrap gap-1.5">{archResult.strengths.map((s, i) => <span key={i} className="text-xs bg-calm text-calm-text px-2.5 py-1 rounded-full">{s}</span>)}</div></div>
              )}
              {archResult.shadow?.length > 0 && (
                <div><p className="text-sm font-medium text-ink mb-1">Shadow to tend</p>
                  <div className="flex flex-wrap gap-1.5">{archResult.shadow.map((s, i) => <span key={i} className="text-xs bg-warmsoft text-warmsoft-text px-2.5 py-1 rounded-full">{s}</span>)}</div></div>
              )}
              {archResult.growth_invitation && <p className="text-sm italic text-brand font-serif text-base leading-relaxed">{archResult.growth_invitation}</p>}
              <Button onClick={() => setArchResult(null)} variant="outline" className="rounded-full border-brand text-brand w-full" data-testid="archetype-retry">Explore again</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Resources;
